import React, { useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Bold, Italic, Heading, List, ListOrdered, Link as LinkIcon, Quote } from 'lucide-react';

// ─── Renderers for ReactMarkdown (hoisted to avoid re-creating per render) ───
const MD_LINK = (props) => (
  <a {...props} target="_blank" rel="noreferrer" className="accent-green hover:underline" />
);
const MD_H1 = (props) => <h2 {...props} className="text-2xl font-semibold text-strong mt-6 mb-2" />;
const MD_H2 = (props) => <h3 {...props} className="text-xl font-semibold text-strong mt-5 mb-2" />;
const MD_H3 = (props) => <h4 {...props} className="text-base font-semibold text-strong mt-4 mb-1.5" />;
const MD_P  = (props) => <p  {...props} className="mb-3" />;
const MD_UL = (props) => <ul {...props} className="list-disc pl-6 mb-3 space-y-1" />;
const MD_OL = (props) => <ol {...props} className="list-decimal pl-6 mb-3 space-y-1" />;
const MD_BLOCKQUOTE = (props) => (
  <blockquote {...props} className="border-l-2 border-default pl-3 italic text-muted my-3" />
);
const MD_CODE = ({ inline, ...props }) => (
  inline
    ? <code {...props} className="px-1 py-0.5 rounded text-xs font-mono" style={{ background: 'var(--bg-base)' }} />
    : <code {...props} className="block p-3 rounded text-xs font-mono whitespace-pre-wrap" style={{ background: 'var(--bg-base)' }} />
);

const MD_COMPONENTS = {
  a: MD_LINK,
  h1: MD_H1, h2: MD_H2, h3: MD_H3,
  p: MD_P,
  ul: MD_UL, ol: MD_OL,
  blockquote: MD_BLOCKQUOTE,
  code: MD_CODE,
};

const REMARK_PLUGINS = [remarkGfm];

/**
 * Read-only markdown renderer used on the public /careers page.
 * Disables raw HTML for safety, supports GFM (tables, autolinks, etc.).
 */
export const MarkdownView = ({ children, className = '', testid }) => (
  <div className={`md-view text-default leading-relaxed ${className}`} data-testid={testid}>
    <ReactMarkdown remarkPlugins={REMARK_PLUGINS} skipHtml components={MD_COMPONENTS}>
      {children || ''}
    </ReactMarkdown>
  </div>
);

// ─── Toolbar button ───
const ToolbarButton = ({ onClick, title, icon: Icon, testid }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    className="btn-secondary px-2 py-1 text-xs inline-flex items-center gap-1"
    data-testid={testid}
  >
    <Icon className="w-3.5 h-3.5" />
  </button>
);

/**
 * Markdown textarea + lightweight toolbar that wraps the current selection.
 * Admin types markdown; public renders via <MarkdownView /> above.
 *
 * Props: value, onChange(value), rows, placeholder, testid
 */
export const MarkdownEditor = ({ value, onChange, rows = 10, placeholder, testid }) => {
  const ref = useRef(null);

  const wrap = (before, after = before, placeholderText = '') => {
    const ta = ref.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const selected = value.substring(start, end) || placeholderText;
    const next = value.substring(0, start) + before + selected + after + value.substring(end);
    onChange(next);
    requestAnimationFrame(() => {
      ta.focus();
      const offset = start + before.length;
      ta.setSelectionRange(offset, offset + selected.length);
    });
  };

  const prefixLines = (prefix) => {
    const ta = ref.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const lineStart = value.lastIndexOf('\n', start - 1) + 1;
    const lineEnd = (() => {
      const idx = value.indexOf('\n', end);
      return idx === -1 ? value.length : idx;
    })();
    const block = value.substring(lineStart, lineEnd);
    const lines = block.split('\n');
    const transformed = lines.map((ln, i) => {
      if (typeof prefix === 'function') return prefix(ln, i);
      return prefix + ln;
    }).join('\n');
    const next = value.substring(0, lineStart) + transformed + value.substring(lineEnd);
    onChange(next);
    requestAnimationFrame(() => {
      ta.focus();
      ta.setSelectionRange(lineStart, lineStart + transformed.length);
    });
  };

  const insertLink = () => {
    const url = window.prompt('Link URL:', 'https://');
    if (!url) return;
    wrap('[', `](${url})`, 'link text');
  };

  return (
    <div data-testid={testid}>
      <div className="flex gap-1.5 flex-wrap mb-1.5">
        <ToolbarButton onClick={() => wrap('**', '**', 'bold text')} title="Bold (**text**)" icon={Bold} testid={`${testid}-btn-bold`} />
        <ToolbarButton onClick={() => wrap('*', '*', 'italic text')} title="Italic (*text*)" icon={Italic} testid={`${testid}-btn-italic`} />
        <ToolbarButton onClick={() => prefixLines('## ')} title="Heading (## text)" icon={Heading} testid={`${testid}-btn-heading`} />
        <ToolbarButton onClick={() => prefixLines('- ')} title="Bullet list" icon={List} testid={`${testid}-btn-bullet`} />
        <ToolbarButton onClick={() => prefixLines((ln, i) => `${i + 1}. ${ln}`)} title="Numbered list" icon={ListOrdered} testid={`${testid}-btn-ol`} />
        <ToolbarButton onClick={() => prefixLines('> ')} title="Quote" icon={Quote} testid={`${testid}-btn-quote`} />
        <ToolbarButton onClick={insertLink} title="Insert link" icon={LinkIcon} testid={`${testid}-btn-link`} />
      </div>
      <textarea
        ref={ref}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        placeholder={placeholder}
        className="input-field w-full px-3 py-2 text-sm leading-relaxed font-mono"
        data-testid={`${testid}-textarea`}
      />
      <p className="text-xs text-faded mt-1">
        Markdown supported. <span className="text-muted">**bold** · *italic* · ## heading · - bullet · [link](url)</span>
      </p>
    </div>
  );
};
